import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp } from 'lucide-react';
import { hallApi, type HallTable } from '../services/hallApi';
import type { DineInMenuThemeTokens } from '../hooks/useDineInMenuTheme';

export function HallCheckPanel({
  data,
  tokens,
  payWith,
  variant = 'inline',
  isLight = false,
  bottomInset = 24,
  mockOnly = false,
  defaultOpen,
  showTips = false,
  onMockPay,
}: {
  data: HallTable;
  tokens: DineInMenuThemeTokens;
  payWith: { tableId?: string; orderId?: string };
  variant?: 'inline' | 'dock';
  isLight?: boolean;
  bottomInset?: number;
  mockOnly?: boolean;
  defaultOpen?: boolean;
  showTips?: boolean;
  onMockPay?: (kind: 'bill' | 'tip', amount?: number) => void;
}) {
  const order = data.order;
  const [open, setOpen] = useState(defaultOpen ?? variant === 'inline');
  const [busy, setBusy] = useState<'bill' | 'tip' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tipAmount, setTipAmount] = useState('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const presets = useMemo(() => {
    const base = order && !order.closed ? order.sum : 0;
    if (!base) return [100, 200, 300];
    return [10, 15].map((p) => Math.max(50, Math.round((base * p) / 100 / 10) * 10));
  }, [order]);

  async function goPay(kind: 'bill' | 'tip', amount?: number) {
    setError(null);
    if (mockOnly) {
      onMockPay?.(kind, amount);
      return;
    }
    setBusy(kind);
    try {
      const res =
        kind === 'bill'
          ? await hallApi.payBill(payWith)
          : await hallApi.payTip({ ...payWith, amount: amount! });
      if (!res.paymentUrl) throw new Error('Нет ссылки на оплату');
      window.location.assign(res.paymentUrl);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Не удалось создать платёж');
      setBusy(null);
    }
  }

  const customTip = Number(tipAmount.replace(',', '.'));
  const itemsCount = order?.items.reduce((s, i) => s + i.amount, 0) ?? 0;
  const amountLabel = !order
    ? null
    : order.canPayBill
      ? `${Math.round(order.remainder)} ₽`
      : `${Math.round(order.sum)} ₽`;

  const details = (
    <>
      {!order ? (
        <p className="text-sm" style={{ color: tokens.mutedSoft }}>
          Открытого заказа пока нет.
        </p>
      ) : (
        <>
          {order.waiterName ? (
            <p className="text-sm mb-3" style={{ color: tokens.mutedSoft }}>
              Официант: {order.waiterName}
            </p>
          ) : null}
          <ul className="space-y-1.5 mb-3">
            {order.items.map((item, i) => (
              <li key={`${item.name}-${i}`} className="flex justify-between gap-3 text-sm">
                <span>
                  {item.name}
                  {item.amount > 1 ? ` ×${item.amount}` : ''}
                </span>
                <span>{Math.round(item.sum)} ₽</span>
              </li>
            ))}
          </ul>
          <div className="flex justify-between text-sm font-semibold mb-1">
            <span>Итого</span>
            <span>{Math.round(order.sum)} ₽</span>
          </div>
          {order.paid > 0 ? (
            <p className="text-xs mb-3" style={{ color: tokens.muted }}>
              Оплачено {Math.round(order.paid)} ₽ · остаток {Math.round(order.remainder)} ₽
            </p>
          ) : (
            <div className="mb-3" />
          )}
          {showTips ? (
            <div>
              <p className="text-[11px] tracking-[0.2em] uppercase mb-2" style={{ color: tokens.muted }}>
                Чаевые
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {presets.map((n) => (
                  <button
                    key={n}
                    type="button"
                    disabled={busy !== null}
                    onClick={() => goPay('tip', n)}
                    className="px-3 py-2 text-sm disabled:opacity-50"
                    style={{ border: `1px solid ${tokens.border}` }}
                  >
                    {n} ₽
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={1}
                  inputMode="decimal"
                  placeholder="Своя сумма"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm bg-transparent"
                  style={{ border: `1px solid ${tokens.border}`, color: tokens.text }}
                />
                <button
                  type="button"
                  disabled={busy !== null || !Number.isFinite(customTip) || customTip < 1}
                  onClick={() => goPay('tip', customTip)}
                  className="px-4 py-2 text-sm font-semibold disabled:opacity-50"
                  style={{ border: `1px solid ${tokens.border}` }}
                >
                  {busy === 'tip' ? '…' : 'Ок'}
                </button>
              </div>
            </div>
          ) : order.canPayBill ? (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => goPay('bill')}
              className="w-full py-3 text-sm font-semibold uppercase tracking-wide disabled:opacity-50"
              style={{ background: tokens.text, color: tokens.bg }}
            >
              {busy === 'bill' ? 'Переход к оплате…' : `Оплатить счёт ${Math.round(order.remainder)} ₽`}
            </button>
          ) : order.closed ? (
            <p className="text-sm" style={{ color: tokens.mutedSoft }}>
              Счёт закрыт.
            </p>
          ) : null}
        </>
      )}
      {error ? <p className="mt-3 text-sm text-red-500">{error}</p> : null}
    </>
  );

  if (variant === 'inline') {
    return (
      <div
        className="mb-8 p-4"
        style={{ border: `1px solid ${tokens.border}`, color: tokens.text, fontFamily: 'Manrope, sans-serif' }}
      >
        <p className="text-[11px] tracking-[0.2em] uppercase mb-1" style={{ color: tokens.muted }}>
          Стол {data.tableNumber ?? '—'}
          {data.sectionName ? ` · ${data.sectionName}` : ''}
        </p>
        {details}
      </div>
    );
  }

  if (!mounted) return null;

  return createPortal(
    <>
      {open ? (
        <div
          className="fixed inset-0"
          style={{ zIndex: 80, background: 'rgba(0,0,0,0.45)' }}
          onClick={() => setOpen(false)}
          aria-hidden
        />
      ) : null}
      <div
        className="fixed left-0 right-0 px-3"
        style={{ zIndex: 90, bottom: bottomInset, fontFamily: 'Manrope, sans-serif' }}
      >
        <div
          className="mx-auto max-w-xl overflow-hidden"
          style={{
            backgroundColor: tokens.bg,
            color: tokens.text,
            border: `1px solid ${tokens.navBorder}`,
            boxShadow: isLight ? '0 8px 28px rgba(0,0,0,0.08)' : 'none',
          }}
        >
          {open ? (
            <div
              className="max-h-[min(62vh,520px)] overflow-y-auto px-4 pt-4 pb-3"
              style={{ borderBottom: `1px solid ${tokens.borderSoft}` }}
            >
              <p className="text-[11px] tracking-[0.2em] uppercase mb-3" style={{ color: tokens.muted }}>
                Стол {data.tableNumber ?? '—'}
                {data.sectionName ? ` · ${data.sectionName}` : ''}
              </p>
              {details}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
            style={{ color: tokens.text }}
            aria-expanded={open}
          >
            <span className="min-w-0">
              <span className="block text-[11px] tracking-[0.18em] uppercase" style={{ color: tokens.muted }}>
                Стол {data.tableNumber ?? '—'}
                {itemsCount > 0 ? ` · ${itemsCount} поз.` : ''}
              </span>
              <span className="block text-sm font-semibold mt-0.5 truncate">
                {!order ? 'Заказ ещё не открыт' : order.closed ? 'Счёт закрыт' : 'Счёт'}
              </span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {amountLabel ? <span className="text-sm font-semibold">{amountLabel}</span> : null}
              <ChevronUp
                size={16}
                className={open ? '' : 'rotate-180'}
                style={{ color: tokens.navInactive }}
              />
            </span>
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
